/**
 * Money input helpers — pair these with `EditableTextCell`'s
 * `sanitize` + `format` props to give any cell the standard money
 * mask: thousands separator + up to 2-decimal cents.
 *
 *   <EditableTextCell sanitize={moneyDigits} format={formatMoneyMask} ... />
 */

/** Strip everything except digits and a single decimal point; cap the
 *  cents to 2 digits. The result is the canonical raw value stored in
 *  form state — e.g. "1234.5", "1234.56", "1234". */
export function moneyDigits(raw: string): string {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const dot = cleaned.indexOf(".");
    if (dot === -1) return cleaned;
    return (
        cleaned.slice(0, dot + 1) +
        cleaned
            .slice(dot + 1)
            .replace(/\./g, "")
            .slice(0, 2)
    );
}

/** Render the canonical raw value with thousands separators. Empty
 *  input renders as empty so placeholders still show through. The
 *  trailing dot is preserved so the user can type "1234." without the
 *  formatter eating their decimal point mid-keystroke. */
export function formatMoneyMask(raw: string): string {
    if (!raw) return "";
    const [whole, decimal] = raw.split(".");
    const wholeFormatted = whole ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
    if (decimal === undefined) return wholeFormatted;
    return `${wholeFormatted}.${decimal}`;
}

/** Same as `moneyDigits` but for percentage-like values where decimals
 *  are common but no thousands separator is wanted. Exposed for
 *  symmetry — Mat. Credit / SIR Credit keep their plain form. */
export function decimalDigits(raw: string): string {
    return moneyDigits(raw);
}
